import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {UserSettingsService} from '../../user-settings.service';
import {SessionService} from '../../authentication/session-service.service';
import {WorkspacePanels} from '../panels-menu/workspace-panels';
import {PanelModel} from '../panel.model';

/**
 * CR-20260928-002 — panel aliases.
 *
 * One alias per panel TYPE, per user, stored as ONE user setting (a JSON object keyed by the
 * `WorkspacePanels` enum NAME, never its number, so a renumbered enum cannot re-point an alias).
 *
 * A tab's displayed name resolves in this order (owner rulings R3/R4):
 *   1. the tab's own name (double-click rename, saved with the layout) — always wins;
 *   2. the alias of the tab's panel type, if set;
 *   3. the panel type's full name (`PanelModel.header`, which stays the default).
 *
 * The alias is a tab-strip concern only: the tab, the tabs dropdown and the re-arrange dialog
 * read it; the add-panel menu keeps the full names (R1).
 */
export type PanelAliases = { [panelTypeName: string]: string };

const PANEL_ALIASES_KEY = 'workspace.panel-aliases';

@Injectable({providedIn: 'root'})
export class PanelAliasesService {

    constructor(
        private readonly _userSettings: UserSettingsService,
        private readonly _session: SessionService
    ) {
        // Another session of the same user saved the aliases: follow it.
        this._userSettings.userSettingChanged$.subscribe(x => {
            if (x.key !== PANEL_ALIASES_KEY) {
                return;
            }
            if (!this._session.sessionData || x.userId !== this._session.sessionData.userId) {
                return;
            }
            this._aliases$.next(PanelAliasesService.normalise(x.value));
        });
    }

    private _loaded = false;
    private readonly _aliases$ = new BehaviorSubject<PanelAliases>({});

    /** The stored aliases (trimmed, empties dropped). Read lazily: the user settings arrive with the login. */
    get aliases(): PanelAliases {
        if (!this._loaded) {
            this._loaded = true;
            const stored = this._userSettings.getValue<PanelAliases>(PANEL_ALIASES_KEY);
            this._aliases$.next(PanelAliasesService.normalise(stored));
        }
        return this._aliases$.value;
    }

    /** Emits the current aliases and every change after (own save or another session's). */
    get aliases$(): Observable<PanelAliases> {
        // touch the getter so the first emission is the stored value, not the empty seed
        // tslint:disable-next-line:no-unused-expression
        this.aliases;
        return this._aliases$.asObservable();
    }

    getAlias(typeCode: WorkspacePanels): string | undefined {
        return this.aliases[WorkspacePanels[typeCode]];
    }

    /** The name a tab shows: own name → alias → full name. */
    displayName(panel: PanelModel): string {
        if (!panel) {
            return '';
        }
        const own = (panel.customHeader || '').trim();
        if (own) {
            return own;
        }
        return this.getAlias(panel.typeCode) || panel.header;
    }

    /** Stores the aliases (trimmed, empties dropped) and republishes to every subscriber. */
    async save(next: PanelAliases): Promise<void> {
        const value = PanelAliasesService.normalise(next);
        this._loaded = true;
        this._aliases$.next(value);
        await this._userSettings.setValue(PANEL_ALIASES_KEY, value);
    }

    private static normalise(raw: any): PanelAliases {
        const result: PanelAliases = {};
        if (!raw || typeof raw !== 'object') {
            return result;
        }
        Object.keys(raw).forEach(k => {
            const v = typeof raw[k] === 'string' ? raw[k].trim() : '';
            if (v) {
                result[k] = v;
            }
        });
        return result;
    }
}
